// Direcciones del directorio (Google vía catwoman) para mostrar. A veces traen el código postal
// repetido en dos tramos — «Carlos Pellegrini 1059 C1009ABU, C1009ABU Cdad. Autónoma de Buenos
// Aires» — o el mismo tramo dos veces («Montevideo, Montevideo, Uruguay»).

// Código postal argentino: CPA (`C1009ABU`) o la forma corta (`C1009`). Los números sueltos de 4
// dígitos no cuentan: son alturas de calle.
const POSTAL_CODE = /\b([A-Z]\d{4})([A-Z]{3})?\b/g;

// Para comparar tramos: sin acentos ni puntos y con las abreviaturas de Google expandidas
// («Cdad. Autónoma» = «Ciudad Autónoma»).
const normalize = (segment) =>
  String(segment)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\bcdad\b\.?/g, 'ciudad')
    .replace(/\bpcia\b\.?/g, 'provincia')
    .replace(/\bsta\b\.?/g, 'santa')
    .replace(/[.\s]+/g, ' ')
    .trim();

// «Departamento de Montevideo» después de «11600 Montevideo»: no agrega nada.
const ADMIN_PREFIX = /^(departamento|depto|provincia) de /;

/**
 * Dirección legible o `null`: sin tramos vacíos ni `undefined`, sin tramos repetidos y con cada
 * código postal una sola vez (queda el último, el que va pegado a la ciudad; `C1009` y `C1009ABU`
 * son el mismo).
 */
export function cleanAddress(address) {
  if (!address) return null;
  let segments = String(address)
    .split(',')
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter((s) => s && s !== 'undefined');

  const seen = [];
  segments = segments.filter((s) => {
    const key = normalize(s);
    const bare = key.replace(ADMIN_PREFIX, '');
    if (seen.includes(key) || (bare !== key && seen.some((prev) => prev === bare || prev.endsWith(` ${bare}`)))) {
      return false;
    }
    seen.push(key);
    return true;
  });

  // Último tramo en el que aparece cada código (por sus cinco primeros caracteres).
  const lastIndex = new Map();
  segments.forEach((s, i) => {
    for (const match of s.matchAll(POSTAL_CODE)) lastIndex.set(match[1], i);
  });
  segments = segments
    .map((s, i) =>
      s
        .replace(POSTAL_CODE, (code, short) => (lastIndex.get(short) === i ? code : ''))
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(Boolean);

  const cleaned = segments.join(', ');
  return cleaned || null;
}

/**
 * Línea de localidad debajo de la dirección («Bolívar, Montevideo»): sin repetidos (contando
 * abreviaturas) y sin lo que la dirección ya dice. En CABA la dirección ya termina en «Cdad. Autónoma
 * de Buenos Aires» y la línea repetía «Cdad. Autónoma de Buenos Aires, Ciudad Autónoma de Buenos Aires».
 */
export function localityLine(address, parts) {
  const inAddress = normalize(address || '');
  const seen = [];
  const kept = [];
  for (const part of parts || []) {
    if (!part) continue;
    const key = normalize(part);
    if (!key || seen.includes(key)) continue;
    seen.push(key);
    if (inAddress.includes(key)) continue;
    kept.push(String(part).trim());
  }
  return kept.join(', ');
}
